import Iframe, { IframeTypeEnum } from 'components/Iframe'
import EFTDisclosureModal from 'components/Modals/EFTDisclosureModal'
import ErrorMessageModal from 'components/Modals/ErrorMessageModal'
import LoaderModal from 'components/Modals/LoaderModal'
import { useNorthCapitalApi } from 'hooks/useNorthCapitalAPI'
import { useRiseUpAPIV1 } from 'hooks/useRiseUpAPIV1'
import queryString from 'query-string'
import React, { useEffect, useRef, useState } from 'react'
import 'react-credit-cards-2/dist/es/styles-compiled.css'
import ReactGA from 'react-ga4'
import { useLocation, useNavigate } from 'react-router-dom'
import { getModifiedUrlWithStep } from 'utils'
import { consoleLog } from 'utils/simpleLogger'
import CompanyLogo from '../../../assets/afbc.png'
import Side1 from '../../../assets/investing-signup-images/shelf.jpeg'
import Button from '../../../components/Button'
import Stepper from '../../../components/Stepper'
import { IFormProps } from '../../../interfaces'

const CreditDebit = (props: IFormProps) => {
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showEFTDisclosureModal, setShowEFTDisclosureModal] = useState(false)
  const isProcessingPayment = useRef(false)

  const { getOnboardProgress, finalizeUserCreation } = useRiseUpAPIV1()
  const { createTrade, initiateCardPayment } = useNorthCapitalApi()
  const navigate = useNavigate()
  const location = useLocation()

  const getFreshBearerToken = async () => {
    const getOnboardProgressRes = await getOnboardProgress(props.getValues('onboardingId'))
    if (getOnboardProgressRes.status === 200) {
      return getOnboardProgressRes.data.accessToken
    }
  }

  const performTrade = async (accessToken: string) => {
    const res = await createTrade(
      props.getValues('investingProfile.accountId'),
      'CREDITCARD',
      props.getValues('investingProfile.totalShares'),
      accessToken,
    )
    consoleLog(res)
    props.setValue('investingProfile.tradeId', Number(res.data.purchaseDetails[1][0].tradeId))
  }

  const finalizeCreationProcess = async () => {
    const res = await finalizeUserCreation(props.getValues('onboardingId'))
    consoleLog(res)
  }

  const performPayment = async (accessToken: string) => {
    const res = await initiateCardPayment(
      props.getValues('investingProfile.accountId'),
      props.getValues('investingProfile.tradeId'),
      accessToken,
    )

    if (res.data.statusCode === '101') {
      isProcessingPayment.current = false
      await finalizeCreationProcess()

      const pathname = location.pathname
      const currentParams = queryString.parse(location.search)
      const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'success_page')
      navigate(updatedUrl)
      props.nextStep('Success')
    } else {
      throw new Error('Payment initiation failed with status code: ' + res.data.statusCode)
    }
  }

  const handleProceedToPayments = async () => {
    // Prevent multiple concurrent payment processing
    if (isProcessingPayment.current) {
      console.log('Payment processing already in progress')
      return
    }

    isProcessingPayment.current = true
    setIsLoading(true)

    try {
      const accessToken = await getFreshBearerToken()
      await performTrade(accessToken)
      await performPayment(accessToken)
    } catch (error: any) {
      console.log(error)
      isProcessingPayment.current = false
      ReactGA.event({
        category: 'Credit Payment page',
        action: `Credit Payment page error`,
        label: JSON.stringify(error),
      })
      setIsLoading(false)
      if ((error as any).response.data._error as string) {
        setError(error.response.data._error)
      } else {
        setError('Unexpected error')
      }
    }
  }

  useEffect(() => {
    props.setValue('entityDetails.legalStructure', 'LLC')
    props.setValue('cc.firstName', props.getValues('basicInfo.firstName'))
    props.setValue('cc.lastName', props.getValues('basicInfo.lastName'))
  }, [props.errors]) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative sm:h-auto sm:p-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side1} alt="Side1" />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="z-10 pb-8 pt-10 text-left text-2xl font-semibold text-gray-900 md:text-4xl lg:pb-10">
          Let's process your payment
        </div>

        {props.getValues('paymentIframe') && props.getValues('paymentIframe') !== '' ? (
          <div className="z-0 mt-[-50px] flex justify-center md:justify-start">
            <Iframe
              source={props.getValues('paymentIframe')}
              title={'Add Credit Card'}
              proceed={handleProceedToPayments}
              type={IframeTypeEnum.CREDIT_CARD}
            />
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-col text-base md:mt-0">
              <div className="mb-2 flex flex-col justify-between md:mb-0 md:flex-row md:justify-start">
                <div className="text-gray-600">Card Type: </div>
                <div className="font-bold md:ml-3">Visa</div>
              </div>

              <div className="mb-2 flex flex-col justify-between md:mb-0 md:flex-row md:justify-start">
                <div className="text-gray-600">Credit Card Number: </div>
                <div className="font-bold md:ml-3">XXXX-XXXX-XXXX-{props.getValues('ccDetails.creditCardNumber')}</div>
              </div>

              <div className="mt-8 flex flex-col sm:w-[100%]">
                <Button
                  btnName={'Initiate Card Payment'}
                  onClickHandler={() => {
                    handleProceedToPayments()
                  }}
                  disabled={false}
                />
              </div>
            </div>
          </>
        )}
        <div className={`z-10 flex flex-col justify-center`}>
          <div></div>
          <div className="mt-4 text-base leading-5 text-gray-500">
            Fees associated with this transaction are non-refundable. By clicking "Submit" you authorize this
            transaction and agree to our{' '}
            <span
              className="cursor-pointer font-semibold text-blue-600 underline"
              onClick={() => window.open('https://ownarmedforcesbrewingco.com/terms/')}
            >
              Terms of Use
            </span>{' '}
            &{' '}
            <span
              className="cursor-pointer font-semibold text-blue-600 underline"
              onClick={() => setShowEFTDisclosureModal(true)}
            >
              EFT Disclosure
            </span>
            .
          </div>

          <div className="mt-8 text-base sm:text-2xl md:text-base"></div>
          <div className="z-50 flex flex-col sm:w-[100%] md:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('8')} />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <ErrorMessageModal
        isVisible={error !== ''}
        onProceed={() => {
          setError('')
        }}
        onCancel={() => {}}
        error={error}
        btn2={'Ok'}
      />
      <EFTDisclosureModal isVisible={showEFTDisclosureModal} setClose={() => setShowEFTDisclosureModal(false)} />
    </div>
  )
}

export default CreditDebit
